import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Axios from "axios";
import * as constants from "../constants";
import Documents from "../services/Documents";
import { fetchDocuments } from "../services/helperFunction";
import { 
  CalendarCheck, 
  Calendar, 
  MapPin, 
  BookOpen, 
  Clock, 
  GraduationCap,
  FileText,
  FolderOpen,
  BookA,
  BookAlert,
  BookCheck
} from "lucide-react";

const ProgramSummary = (props) => {
  const [activeTab, setActiveTab] = useState("1");
  let { id } = useParams();
  const [state, setState] = useState({
    courseId: undefined,
    enrolmentSummary: undefined,
  });

  const [resourceDocuments, setResourceDocuments] = useState([]);
  const [myDocuments, setMyDocuments] = useState([]);
  const [loadingResources, setLoadingResources] = useState(false);
  const [loadingMyDocuments, setLoadingMyDocuments] = useState(false);

  useEffect(() => {
    const fetchProgramSummary = async () => {
      try {
        const response = await Axios.get(
          constants.BASEURL + "Student/ProgramSummary/" + id
        );
        const enrolmentSummary = response.data;
        setState({
          enrolmentSummary: enrolmentSummary,
          courseId: enrolmentSummary.courseId,
        });

        if (enrolmentSummary.courseId) {
          setLoadingResources(true);
          const resources = await fetchDocuments(enrolmentSummary.courseId, 9);
          setResourceDocuments(resources);
          setLoadingResources(false);
        }

        if (id) {
          setLoadingMyDocuments(true);
          const docs = await fetchDocuments(id, 13);
          setMyDocuments(docs);
          setLoadingMyDocuments(false);
        }
      } catch (error) {
        console.error("Error fetching program summary:", error);
        setLoadingResources(false);
        setLoadingMyDocuments(false);
      }
    };

    fetchProgramSummary();
  }, [id]);

  const getAttendanceColor = (percentage) => {
    if (percentage >= 80) return "text-green-600";
    if (percentage >= 60) return "text-amber-600";
    return "text-red-600";
  };

  const getAttendanceBg = (percentage) => {
    if (percentage >= 80) return "bg-green-500";
    if (percentage >= 60) return "bg-amber-500";
    return "bg-red-500";
  };

  const parseWeeks = (value) => {
    const weeks = Number(value);
    return Number.isFinite(weeks) ? weeks : 0;
  };

  // Loading Skeleton
  const LoadingSkeleton = () => (
    <div className="animate-pulse space-y-6">
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <div className="h-6 w-48 bg-gray-200 rounded mb-4"></div>
        <div className="h-4 w-full bg-gray-200 rounded mb-2"></div>
        <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="h-5 w-32 bg-gray-200 rounded mb-4"></div>
          <div className="h-20 w-full bg-gray-200 rounded"></div>
        </div>
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="h-5 w-32 bg-gray-200 rounded mb-4"></div>
          <div className="h-20 w-full bg-gray-200 rounded"></div>
        </div>
      </div>
    </div>
  );

  const renderProgramSummary = () => {
    const { enrolmentSummary } = state;
    const hideTimetableAndAttendance = Boolean(window.configs?.hideTimetableAndAttendance);

    if (!enrolmentSummary) {
      return <LoadingSkeleton />;
    }

    const subjectProgress = enrolmentSummary.totalSubjects > 0
      ? Math.round((enrolmentSummary.completedSubjects / enrolmentSummary.totalSubjects) * 100)
      : 0;

      const notCounted = enrolmentSummary?.notCountedModules ?? 0;
      const studyWeeks = parseWeeks(enrolmentSummary?.studyWeeks);
      const academicHolidayWeeks = parseWeeks(enrolmentSummary?.holidayWeeks);
      const studentHolidayWeeks = parseWeeks(enrolmentSummary?.studentHolidayWeeks);
      const totalDurationWeeks = studyWeeks + academicHolidayWeeks + studentHolidayWeeks;

    return (
      <div className="space-y-6">
        {/* Header Card */}
        <div className="bg-white rounded-md border border-gray-200 p-4 sm:p-6 lg:p-8 shadow-md space-y-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            {/* Left - Course Info */}
            <div className="flex-1 min-w-0">
              {/* Badges */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-50 text-indigo-700 text-sm font-medium rounded-full">
                  <GraduationCap className="w-4 h-4" />
                  {enrolmentSummary.courseCode}
                </span>
                <span className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
                  #{enrolmentSummary.enrolmentNo}
                </span>
                {/* <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-700 text-sm font-medium rounded-full">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                  Active
                </span> */}
              </div>

              {/* Course Name */}
              <h2 className="text-xl font-semibold text-gray-900 mb-2 break-words sm:truncate">
                {enrolmentSummary.courseName}
              </h2>

              {/* Campus */}
              <div className="flex items-center gap-2 text-gray-500 min-w-0">
                <MapPin className="w-4 h-4 shrink-0" />
                <span className="text-sm truncate">{enrolmentSummary.campusName} Campus</span>
              </div>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Start Date</p>
                <p className="text-sm font-medium text-gray-900 truncate">{enrolmentSummary.startDate}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center shrink-0">
                <Calendar className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">End Date</p>
                <p className="text-sm font-medium text-gray-900">{enrolmentSummary.endDate}</p>
              </div>
            </div>

            {!hideTimetableAndAttendance && (
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Time Table</p>
                  <p className="text-sm font-medium text-gray-900 break-all">
                    {enrolmentSummary.timeTableName || "-"}
                  </p>
                </div>
              </div>
            )}

            {!hideTimetableAndAttendance && (
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                  <CalendarCheck className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Current Attendance</p>
                  <p className={`text-sm font-medium ${getAttendanceColor(enrolmentSummary.currentAttendance)}`}>
                    {enrolmentSummary.currentAttendance}%
                  </p>
                </div>
              </div>
            )}

            {!hideTimetableAndAttendance && (
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 bg-yellow-50 rounded-lg flex items-center justify-center shrink-0">
                  <CalendarCheck className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Overall Attendance</p>
                  <p className={`text-sm font-medium ${getAttendanceColor(enrolmentSummary.overAllAttendance)}`}>
                    {enrolmentSummary.overAllAttendance}%
                  </p>
                </div>
              </div>
            )}


            <div className="flex items-center gap-3 min-w-0 lg:col-span-1">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Study Period</p>
                <p className="text-sm font-medium text-gray-900">
                  ({totalDurationWeeks} total duration) {studyWeeks} study weeks,{" "}
                  {academicHolidayWeeks} academic holiday weeks
                  {studentHolidayWeeks > 0 && `, ${studentHolidayWeeks} student holiday weeks`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center shrink-0">
                <BookA className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Subjects</p>
                <p className="text-sm font-medium text-gray-900">
                  {(enrolmentSummary?.completedSubjects ?? 0) + (enrolmentSummary?.totalSubjects ?? 0)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center shrink-0">
                <BookCheck className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-xs text-green-500">Successful Subjects</p>
                <p className="text-sm font-medium text-green-900">
                  {(enrolmentSummary?.completedSubjects ?? 0)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 bg-yellow-50 rounded-lg flex items-center justify-center shrink-0">
                <BookOpen className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-xs text-yellow-500">Yet to complete</p>
                <p className="text-sm font-medium text-yellow-900">
                  {(enrolmentSummary?.totalSubjects ?? 0)- (enrolmentSummary?.completedSubjects ?? 0) }
                </p>
              </div>
            </div>
                
            {notCounted > 0 && (
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center shrink-0">
                  <BookAlert className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xs text-red-500">Not counted due to CT, RPL , RCG</p>
                  <p className="text-sm font-medium text-red-900">
                    {(enrolmentSummary?.notCountedModules ?? 0) }
                  </p>
                </div>
              </div>
            )}
            
            
          </div>

        </div>

        {/* Academic Progress Card
        <div className="bg-white rounded-xl border border-blue-200 p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="font-semibold text-gray-900">Academic Progress</h3>
          </div>

          <div className="space-y-4">
            Subjects
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">Subjects Completed</span>
                <span className="text-sm font-semibold text-gray-900">
                  {enrolmentSummary.completedSubjects || 0} / {enrolmentSummary.totalSubjects || 0}
                </span>
              </div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-500 rounded-full transition-all duration-500"
                  style={{ width: `${subjectProgress}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-400 mt-1">{subjectProgress}% Complete</p>
            </div>

            UOS
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Completed UOS</p>
                <p className="text-lg font-semibold text-gray-900">{enrolmentSummary.completedUOS || 0}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Total UOS</p>
                <p className="text-lg font-semibold text-gray-900">{enrolmentSummary.totalUOS || 0}</p>
              </div>
            </div>
          </div>
        </div> */}

        {/* Documents Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Resources */}
          {(state.courseId && (loadingResources || resourceDocuments.length > 0)) && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-md min-w-0">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 bg-green-50 rounded-lg flex items-center justify-center">
                  <FolderOpen className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Resources</h3>
                  <p className="text-xs text-gray-500">Available to download</p>
                </div>
              </div>
              <Documents documents={resourceDocuments} isLoading={loadingResources} headingText="Resources" />
            </div>
          )}

          {/* My Documents */}
          {(id && (loadingMyDocuments || myDocuments.length > 0)) && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-md">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 bg-indigo-50 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">My Documents</h3>
                  <p className="text-xs text-gray-500">Submitted so far</p>
                </div>
              </div>
              <Documents documents={myDocuments} isLoading={loadingMyDocuments} headingText="My Documents" />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="px-4 sm:px-4 py-4">
      <div className="mx-auto w-full">
        {renderProgramSummary()}
      </div>
    </div>
  );
};

export default ProgramSummary;